
import React, { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { EstimateFormValues } from '../types/estimateFormTypes';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface EstimateSettingsFieldsProps {
  form: UseFormReturn<EstimateFormValues>;
  companyVatStatus?: { is_vat_registered: boolean };
}

const EstimateSettingsFields: React.FC<EstimateSettingsFieldsProps> = ({ form, companyVatStatus }) => {
  const { profile } = useAuth();
  const [vatRegistered, setVatRegistered] = useState<boolean | null>(null);
  const [companyVatRate, setCompanyVatRate] = useState<number>(15);

  // Automatically fetch company VAT registration and rate if not provided via prop
  useEffect(() => {
    if (typeof companyVatStatus?.is_vat_registered === 'boolean') {
      setVatRegistered(companyVatStatus.is_vat_registered);
      // Reset VAT fields when company is not VAT registered
      if (!companyVatStatus.is_vat_registered) {
        form.setValue('tax_percentage', 0);
        form.setValue('tax_inclusive', false);
      }
      return;
    }
    // Try to get company info for current user if not passed in as prop
    const fetchCompanyVat = async () => {
      if (!profile?.company_id) {
        setVatRegistered(false);
        return;
      }
      const { data, error } = await supabase
        .from('companies')
        .select('is_vat_registered, "VAT Rate"')
        .eq('id', profile.company_id)
        .single();
      
      const isVatReg = data?.is_vat_registered ?? false;
      const vatRate = data?.["VAT Rate"] ?? 15;
      
      setVatRegistered(isVatReg);
      setCompanyVatRate(vatRate);
      
      // Reset VAT fields when company is not VAT registered
      if (!isVatReg) {
        form.setValue('tax_percentage', 0);
        form.setValue('tax_inclusive', false);
      } else {
        // Set the default tax percentage to the company's VAT rate
        const currentTaxPercentage = form.getValues('tax_percentage');
        if (currentTaxPercentage === 0 || currentTaxPercentage === 15) {
          form.setValue('tax_percentage', vatRate);
        }
      }
    };
    fetchCompanyVat();
  }, [companyVatStatus, profile?.company_id, form]);

  // If not loaded yet, return null or a skeleton loader
  if (vatRegistered === null) 
    return <div className="mb-4 text-gray-400">Loading estimate settings...</div>;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {vatRegistered && (
          <FormField
            control={form.control}
            name="tax_percentage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>VAT Percentage (%) - Company Default: {companyVatRate}%</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder={companyVatRate.toString()}
                    {...field}
                    value={field.value ?? ''}
                    onChange={e => {
                      const val = e.target.value;
                      field.onChange(val === '' ? '' : parseFloat(val));
                    }}
                    onBlur={e => {
                      const val = parseFloat(e.target.value);
                      if (isNaN(val) || e.target.value === '') {
                        field.onChange(companyVatRate);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      {vatRegistered && (
        <FormField
          control={form.control}
          name="tax_inclusive"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">VAT Inclusive</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Are the line item prices inclusive of VAT?
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </>
  );
};

export default EstimateSettingsFields;
